/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Where a chosen-but-not-yet-uploaded file belongs in the form.
 *
 * `FileUploadInput` hands a picked File up to the screen rather than uploading
 * it, so that abandoning a half-filled wizard leaves nothing in storage. The
 * screen holds those files until the save, uploads them, and then has to put
 * each resulting URL back where it came from. The form is a tree — sponsors and
 * activities are lists — so "where it came from" needs a name.
 *
 * These keys ARE that name, and they double as the `inputProps.name` the upload
 * component reports back. Flat for an event-level field, `kind:index:column` for
 * anything inside a list.
 */

export const COVER_FIELD = 'cover_image_url';

export const sponsorFileField = (index, column) => `sponsor:${index}:${column}`;
/**
 * `column` may be a dotted PATH within the entry, because a file's value does
 * not always sit on the entry itself: an activity's GPX belongs to its route
 * (`route.gpx_file_url`), which is a row of its own. Writing it flat put the
 * uploaded URL on the activity, where nothing reads it and the batch builder —
 * which takes route columns from `activity.route` — silently dropped it.
 */
export const activityFileField = (index, column) => `activity:${index}:${column}`;

/**
 * The storage server's `file_type`, which decides whether the upload comes back
 * as a directly-servable public URL or as a proxied download.
 *
 * Images are the only kind that gets a direct public URL today, so anything else
 * — a GPX route, say — is declared honestly rather than mislabelled to sneak
 * past that: a wrong type here would put an unusable link in front of a
 * participant instead of a missing one, which is harder to notice.
 */
export function uploadFileTypeFor(field) {
  // 'route', not 'gpx'. The storage server's file_type is a CATEGORY —
  // image, video, text, document, archive, route — not an extension, and it
  // refuses anything outside that list. Sending the extension is what made
  // every GPX upload fail with "Invalid file type".
  if (/gpx/i.test(field)) return 'route';
  return /(image|logo|cover)/i.test(field) ? 'image' : 'other';
}

/**
 * Whether this file needs a directly-servable public URL rather than the
 * OAuth-proxied download the storage client hands non-images by default.
 *
 * True for anything a PUBLIC page links to. A GPX route is the case in point: a
 * participant tapping "download the course" has no token, so a proxied link
 * gives them a 401 instead of a file.
 */
export function prefersPublicUrl(field) {
  return /gpx/i.test(field);
}

/**
 * Immutably sets one list entry's column, which may be a dotted path.
 *
 * Returns the list unchanged if the entry is absent, and refuses to CREATE an
 * intermediate object: a GPX for an activity that has no route means the form
 * has moved on since the file was chosen, and inventing a route row to hold the
 * URL would write a route nobody asked for.
 */
function setInList(list, index, column, value) {
  if (!Array.isArray(list) || !list[index]) return list;

  const path = String(column).split('.');

  return list.map((entry, position) => {
    if (position !== index) return entry;
    if (path.length === 1) return { ...entry, [path[0]]: value };

    const [head, ...rest] = path;
    const branch = entry[head];
    if (!branch || typeof branch !== 'object') return entry;

    // One level of nesting is all the form has; deeper paths recurse through
    // the same rule rather than being special-cased here.
    return {
      ...entry,
      [head]: rest.length === 1
        ? { ...branch, [rest[0]]: value }
        : setInList([branch], 0, rest.join('.'), value)[0],
    };
  });
}

/**
 * Splices freshly-uploaded URLs back into a copy of the form.
 *
 * An unrecognised key is ignored rather than thrown on: the alternative is a
 * save that fails outright because one optional logo had a stale key, after the
 * file has already been uploaded.
 */
export function applyUploadedUrls(form, urls) {
  return Object.entries(urls || {}).reduce((next, [field, url]) => {
    if (!url) return next;

    const parts = field.split(':');
    if (parts.length === 1) return { ...next, [field]: url };

    const [kind, rawIndex, column] = parts;
    const index = Number(rawIndex);
    if (!Number.isInteger(index) || !column) return next;

    if (kind === 'sponsor') {
      return { ...next, sponsors: setInList(next.sponsors, index, column, url) };
    }
    if (kind === 'activity') {
      return { ...next, activities: setInList(next.activities, index, column, url) };
    }
    return next;
  }, { ...form });
}

/**
 * Keeps the chosen-but-not-yet-uploaded files in step with the form.
 *
 * Files are keyed by list POSITION (`activity:1:route.gpx_file_url`), and the
 * form's lists change under them. Without this, removing activity 1 left
 * activity 2's file under key 2 — which then pointed at nothing (the file
 * uploaded anyway, an orphan) or, with more entries, at the WRONG entry. And an
 * activity switched from a route to a venue kept its GPX queued, so the file
 * was uploaded for a route row that is never written.
 *
 * Given the reducer action a step just dispatched, returns the pending map
 * re-keyed to match the form after it. Anything else passes through unchanged
 * (the same object, so React skips the re-render).
 */
export function reconcilePendingUploads(pending, action) {
  if (!pending || !action) return pending;

  const reindex = (kind, removed) => {
    let changed = false;
    const next = {};
    Object.entries(pending).forEach(([key, file]) => {
      const [k, rawIndex, ...rest] = key.split(':');
      const index = Number(rawIndex);
      if (k !== kind || !Number.isInteger(index) || rest.length === 0) {
        next[key] = file;
        return;
      }
      if (index === removed) { changed = true; return; }
      if (index > removed) {
        changed = true;
        next[[k, index - 1, ...rest].join(':')] = file;
        return;
      }
      next[key] = file;
    });
    return changed ? next : pending;
  };

  if (action.type === 'removeActivity') return reindex('activity', action.index);
  if (action.type === 'removeSponsor') return reindex('sponsor', action.index);

  // Leaving the route model drops the route, so its GPX has nowhere to go.
  if (action.type === 'updateActivity' && action.field === 'location_model'
    && action.value !== 'ROUTE_BASED') {
    const prefix = `activity:${action.index}:route.`;
    const keys = Object.keys(pending).filter((key) => key.startsWith(prefix));
    if (!keys.length) return pending;
    const next = { ...pending };
    keys.forEach((key) => { delete next[key]; });
    return next;
  }

  return pending;
}
